import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/AdBanner.css';
import { baseUrl } from '../config';

import firstimage from '../assets/img/post-product b.png';
import secondimage from '../assets/img/logomini.png';
import thirdimage from '../assets/img/crew.png';

// Sponsor click-tracking + public sponsor-list backend (Phase 1 + addendum).
// Local constant, matching the pattern already used in EnterpriseSupport.jsx
// for this same backend.
const SPONSOR_API_URL = "https://pilocoustomersupport.onrender.com";

const LOCAL_IMAGES = [firstimage, secondimage, thirdimage];
const SHADOW_COLORS = ["14, 184, 166", "99, 102, 241", "236, 72, 153"];

// Used only if GET /sponsors/active fails or returns no active sponsors, so
// the banner never renders empty. These are decorative placeholder slides,
// not real sponsors - their slugs won't match a real Sponsor row, so a click
// on one of these will 404 against /sponsors/click.
const FALLBACK_ADS = [
  {
    id: 'fallback-1',
    image: firstimage,
    title: "Now Showing",
    description: "Experience the magic of cinema in IMAX",
    shadowColor: SHADOW_COLORS[0],
    slug: "now-showing"
  },
  {
    id: 'fallback-2',
    image: secondimage,
    title: "Coming Soon",
    description: "Reserve your premiere tickets today",
    shadowColor: SHADOW_COLORS[1],
    slug: "coming-soon"
  },
  {
    id: 'fallback-3',
    image: thirdimage,
    title: "Special Screenings",
    description: "Director's cut with exclusive content",
    shadowColor: SHADOW_COLORS[2],
    slug: "special-screenings"
  }
];

const AdBanner = ({ interval = 5000 }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [ads, setAds] = useState(FALLBACK_ADS);
  const navigate = useNavigate();

  // Fetch the real, active sponsor list (Phase 1 addendum: GET /sponsors/active
  // is public - {slug, name} only, no destination_url/UTM data). On success,
  // real sponsors are merged in front of FALLBACK_ADS so the fallback slides
  // keep rotating too, rather than being replaced outright. Falls back to
  // FALLBACK_ADS-only on any failure or empty result.
  useEffect(() => {
    let cancelled = false;

    const loadActiveSponsors = async () => {
      try {
        const res = await fetch(`${SPONSOR_API_URL}/sponsors/active`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data) && data.length > 0) {
          const realAds = data.map((sponsor, index) => ({
            id: sponsor.slug,
            image: LOCAL_IMAGES[index % LOCAL_IMAGES.length],
            title: sponsor.name,
            // GET /sponsors/active intentionally only returns {slug, name} -
            // no description field exists to show here.
            description: null,
            shadowColor: SHADOW_COLORS[index % SHADOW_COLORS.length],
            slug: sponsor.slug,
          }));
          // Real sponsors first (priority content) - flagged as a design
          // choice, not silently assumed; fallback ads still rotate in
          // after them instead of disappearing.
          setAds([...realAds, ...FALLBACK_ADS]);
        }
      } catch (err) {
        console.error('Failed to load active sponsors, using fallback ads:', err);
      }
    };

    loadActiveSponsors();
    return () => { cancelled = true; };
  }, []);

  // Keep currentSlide in bounds whenever the ads list changes size/identity.
  useEffect(() => {
    setCurrentSlide(0);
  }, [ads]);

  const handleLearnMoreClick = async (ad) => {
    const token = localStorage.getItem('access_token');

    if (!token) {
      localStorage.setItem('pending_sponsor_redirect', JSON.stringify({
        slug: ad.slug,
        expiry: Date.now() + 10 * 60 * 1000, // 10 minutes
      }));
      navigate('/login');
      return;
    }

    try {
      const userIdRes = await fetch(`${baseUrl}/getUserIDByToken`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { user_id } = await userIdRes.json();

      const clickRes = await fetch(`${SPONSOR_API_URL}/sponsors/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sponsor_slug: ad.slug, pb_user_id: user_id }),
      });
      if (!clickRes.ok) throw new Error(`Sponsor click failed: ${clickRes.status}`);

      const { destination_url } = await clickRes.json();
      window.location.href = destination_url;
    } catch (err) {
      console.error('Sponsor click tracking failed:', err);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % ads.length);
    }, interval);
    return () => clearInterval(timer);
  }, [ads.length, interval]);

  const goToSlide = (index) => setCurrentSlide(index);
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % ads.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + ads.length) % ads.length);

  // ads[currentSlide] can be transiently out of bounds for one render
  // whenever ads shrinks (the live fetch can return fewer items than
  // FALLBACK_ADS) - the reset-to-0 effect above always runs one render
  // too late to prevent that render from crashing, so this has to be a
  // guard at the read site itself, not just the effect.
  const activeAd = ads[currentSlide] || ads[0] || FALLBACK_ADS[0];

  return (
    <div className="ad-banner-wrapper">
      <div
        className="ad-banner-container"
        style={{
          boxShadow: `
            0 10px 30px -5px rgba(${activeAd.shadowColor}, 0.3),
            0 20px 60px -10px rgba(${activeAd.shadowColor}, 0.2),
            0 30px 90px -15px rgba(${activeAd.shadowColor}, 0.1)
          `
        }}
      >
        <div className="ad-banner-film-reel">
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i} className="ad-banner-reel-dot" />
          ))}
        </div>

        <div
          className="ad-banner-slides"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {ads.map((ad, index) => (
            <div key={ad.id} className="ad-banner-slide">
              <img
                src={ad.image}
                alt={ad.title}
                className="ad-banner-image"
                loading="eager" // Add eager loading for better performance
                onError={(e) => {
                  e.target.onerror = null; // Prevent infinite loop
                  e.target.src = "/api/placeholder/1200/600"; // Fallback image
                }}
              />
              <div className="ad-banner-content">
                <h3 className="ad-banner-title">{ad.title}</h3>
                {ad.description && <p className="ad-banner-description">{ad.description}</p>}
                <button className="ad-banner-button" onClick={() => handleLearnMoreClick(ad)}>Learn More</button>
              </div>
            </div>
          ))}
        </div>

        <button onClick={prevSlide} className="ad-banner-prev">&larr;</button>
        <button onClick={nextSlide} className="ad-banner-next">&rarr;</button>

        <div className="ad-banner-dots">
          {ads.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`ad-banner-dot ${currentSlide === index ? 'active' : ''}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdBanner;
